import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { ClearButton } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2, MBImage } from '@shared/components/image';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { useFieldConflictContext, useManagedComponents } from '@shared/scenes/entity/context';
import {
  getComponentIconWithFallback,
  getComponentLabel,
  getComponentPlaceholder,
  isDisplayableComponent,
  isManagingInputComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { EditFieldContainerProps } from './EditFieldContainerProps';
import classNames from './EditFieldContainer.module.css';

export function EditFieldContainer({
  component,
  children,
  icon = getComponentIconWithFallback(component),
  iconUrl,
  label = getComponentLabel(component),
  placeholder = getComponentPlaceholder(component),
  header,
  footer,
  subfooter,
  notes,
  files,
  showIcon = true,
  showLabel = true,
  showPlaceholder = false,
  showClearButton = false,
  error,
  helpText,
  onPress,
  onClear,
  testID = getTestId('card-field-cards', component.id),
  anchorName,
  largerInput = true,
  isGrayScale = false,
  isItalic = false,
  isReadOnly = false,
  style,
  printContent,
  placeholderColor,
}: EditFieldContainerProps) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const conflictingComponentIds = useFieldConflictContext();
  const managedComponents = useManagedComponents(component);
  const isConflicted = conflictingComponentIds.includes(component.id);
  const isMobile = !isDesktop;
  const isDisplayable = isDisplayableComponent(component);
  const shouldHaveIcon = showIcon && isDisplayable;
  const renderIconUrl = showIcon && shouldHaveIcon && !isNil(iconUrl);
  const renderIcon = showIcon && shouldHaveIcon && !isNil(icon) && !renderIconUrl;
  const renderLabel = showLabel && isDisplayable && !isNil(label);
  const renderPlaceholder = showPlaceholder && isDisplayable && !isNil(placeholder);
  const renderClearButton = showClearButton && !isNil(onClear);
  const hasError = !!error;
  // Managing components (e.g. DATE_RANGE) have no `required` of their own — it lives on the
  // managed children they own. Required when ANY child is, matching how the on-submit error is
  // derived in useManagedComponentRequiredErrors, so the hint and the error always agree.
  const isRequired = isManagingInputComponent(component)
    ? managedComponents.some(managed => 'required' in managed && managed.required === true)
    : 'required' in component && !!component.required;
  let helpContent: string | null = null;
  if (isRequired && helpText && !isReadOnly) {
    helpContent = i18n('card.form.fieldHelp.requiredWithHelp', { text: helpText });
  } else if (isRequired && !isReadOnly) {
    helpContent = i18n('card.form.fieldHelp.required');
  } else if (helpText) {
    helpContent = helpText;
  }

  const iconSize = isMobile && largerInput ? grid(4) : grid(3);
  const imageIconSize = isMobile && largerInput ? grid(3) : grid(2.5);

  return (
    <>
      <PrintContent label={label} content={printContent} />
      <View testID={testID} className={[classNames.container, 'no-print']} style={style}>
        {renderLabel && (
          <Text size="overline" weight="semibold" danger={hasError} className={classNames.label}>
            {label}
          </Text>
        )}
        <EditFieldWrapper
          isReadOnly={isReadOnly}
          hasError={hasError}
          isGrayScale={isGrayScale}
          isItalic={isItalic}
          onPress={onPress}
          label={label}
        >
          {header && <View className={classNames.header}>{header}</View>}
          <View className={[classNames.row, isMobile && largerInput && classNames.rowPadded]}>
            {renderIcon && (
              <View className={classNames.icon}>
                <MBIconV2
                  name={icon}
                  size={iconSize}
                  color={hasError ? colors.error.foreground : colors.accent}
                />
              </View>
            )}
            {renderIconUrl && (
              <View className={classNames.icon}>
                <MBImage
                  source={{ uri: iconUrl }}
                  style={{ width: imageIconSize, height: imageIconSize }}
                />
              </View>
            )}
            <View className={classNames.content}>
              {renderPlaceholder && (
                <View
                  className={classNames.placeholder}
                  style={placeholderColor ? { color: placeholderColor } : undefined}
                >
                  {placeholder}
                </View>
              )}
              {children}
            </View>
            {renderClearButton && (
              <View className={classNames.clearButton}>
                <ClearButton onClick={onClear} hasError={hasError} />
              </View>
            )}
          </View>
          {notes && <View className={classNames.notes}>{notes}</View>}
          {files && <View className={classNames.files}>{files}</View>}
          {footer && <View className={classNames.footer}>{footer}</View>}
          <View style={{ anchorName }} className={classNames.anchor} />
        </EditFieldWrapper>
        {isConflicted && (
          <View className={classNames.subfooter}>
            <WarningSubfooter
              showWarning
              message={i18n('card.update.remoteConflictFieldUpdated')}
            />
          </View>
        )}
        {subfooter && <View className={classNames.subfooter}>{subfooter}</View>}
        {hasError && (
          <View className={classNames.error}>
            <Text size="body2" danger>
              {error}
            </Text>
          </View>
        )}
        {!hasError && helpContent && (
          <View className={classNames.helpText}>
            <Text size="body2" inactive>
              {helpContent}
            </Text>
          </View>
        )}
      </View>
    </>
  );
}

function EditFieldWrapper({
  children,
  onPress,
  hasError,
  isReadOnly,
  isGrayScale,
  isItalic,
  label,
}: Pick<
  EditFieldContainerProps,
  'children' | 'onPress' | 'isGrayScale' | 'isItalic' | 'isReadOnly' | 'label'
> & {
  hasError?: boolean;
}) {
  const isClickable = !isNil(onPress) && !isReadOnly;

  return (
    <View
      className={[
        classNames.wrapper,
        isReadOnly && classNames.wrapperReadOnly,
        hasError && classNames.wrapperError,
        isGrayScale && classNames.wrapperGrayScale,
        isItalic && classNames.wrapperItalic,
      ]}
      data-slot="edit-field-wrapper"
    >
      {/* Sibling overlay instead of a wrapping <button>: interactive children
          (e.g. ClearButton) would be invalid DOM inside a button element. */}
      {isClickable && (
        <button
          type="button"
          className={classNames.pressOverlay}
          onClick={onPress}
          aria-label={label}
        />
      )}
      {children}
    </View>
  );
}

interface PrintContentProps {
  label?: string | null;
  content?: string | null;
}

export function PrintContent({ label, content }: PrintContentProps) {
  const renderPrintContent = !isNil(content) && !isEmpty(content);

  if (!renderPrintContent) {
    return null;
  }

  return (
    <div className="print-only print-content-section">
      {!isNil(label) && (
        <div>
          <Text size="overline" weight="semibold">
            {label}
          </Text>
        </div>
      )}
      <div>
        <Text size="body1">{content}</Text>
      </div>
    </div>
  );
}
